import React, { useEffect, useState, useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ResourceType,
  useFetchResourcesByType,
  Resource,
} from '../../hooks/useFetchResourcesByType';
import { Role } from '../../hooks/useFetchRoles';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { z } from 'zod';

// TypeScript interfaces
interface RoleAssignmentFormProps {
  roles: Record<string, Role>;
  onSubmit: (data: RoleAssignment) => void;
  onCancel: () => void;
}

export interface RoleAssignment {
  role_key: string;
  resource_type?: string;
  resource_id?: string;
  resource_label?: string;
}

// Constants
const VALID_RESOURCE_TYPES: ResourceType[] = [
  'Study',
  'StudySite',
  'Submission',
];

// Helper function for consistent error message styling
const ErrorMessage: React.FC<{ message: string | undefined; id?: string }> = ({
  message,
  id,
}) => {
  if (!message) return null;
  return (
    <p className="text-sm text-red-600 mt-1" id={id}>
      {message}
    </p>
  );
};

// Helper function to add spaces between capital letters
const addSpacesBetweenCapitals = (text: string): string => {
  return text.replace(/([a-z])([A-Z])/g, '$1 $2');
};

// Create schema factory function to handle dynamic validation
const createRoleAssignmentSchema = (roles: Record<string, Role>) =>
  z
    .object({
      role_key: z.string().min(1, 'Role is required'),
      resource_type: z.string().optional(),
      resource_id: z.string().optional(),
      resource_label: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const selectedRole = roles[data.role_key];
      const hasAllowedResources = selectedRole?.allowed_resources?.some(
        resource => resource !== null
      );

      if (hasAllowedResources) {
        if (!data.resource_type) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Resource type is required',
            path: ['resource_type'],
          });
        }
        if (!data.resource_id) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Resource ID is required',
            path: ['resource_id'],
          });
        }
      }
    });

type RoleAssignmentSchema = z.infer<
  ReturnType<typeof createRoleAssignmentSchema>
>;

const RoleAssignmentForm: React.FC<RoleAssignmentFormProps> = ({
  roles,
  onSubmit,
  onCancel,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const schema = useMemo(() => createRoleAssignmentSchema(roles), [roles]);
  const form = useForm<RoleAssignmentSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      role_key: '',
      resource_type: '',
      resource_id: '',
      resource_label: '',
    },
  });

  const {
    formState: { errors },
  } = form;

  const roleKey = form.watch('role_key');
  const allowedResources =
    roleKey && roles[roleKey]?.allowed_resources
      ? roles[roleKey].allowed_resources.filter(resource => resource !== null)
      : [];

  const {
    resources,
    isLoading: isLoadingResources,
    fetchResources,
    error: fetchError,
  } = useFetchResourcesByType<Resource>();

  const resourceType = form.watch('resource_type');

  // Reset dependent fields when role changes
  useEffect(() => {
    if (roleKey) {
      form.reset({
        ...form.getValues(),
        resource_type: '',
        resource_id: '',
        resource_label: '',
      });
    }
  }, [roleKey, form]);

  // Reset resource fields when resource type changes
  useEffect(() => {
    if (resourceType) {
      form.setValue('resource_id', '');
      form.setValue('resource_label', '');

      const abortController = new AbortController();
      // Validate resourceType is a valid ResourceType before casting
      if (VALID_RESOURCE_TYPES.includes(resourceType as ResourceType)) {
        fetchResources(resourceType as ResourceType, {
          signal: abortController.signal,
        });
      }
      return () => abortController.abort();
    }
  }, [resourceType, fetchResources, form]);

  const handleFormSubmit = async (data: RoleAssignment) => {
    const resourceLabel = resources?.find(
      resource => resource.id === data.resource_id
    )?.name;
    setIsSubmitting(true);
    try {
      await onSubmit({
        ...data,
        resource_label: resourceLabel,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="role" className="text-sm font-medium text-gray-700">
            Role
          </Label>
          <Controller
            name="role_key"
            control={form.control}
            render={({ field }) => {
              return (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(roles).map(role => (
                      <SelectItem key={role} value={role}>
                        {roles[role].name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            }}
          />
          {errors.role_key && (
            <ErrorMessage message={errors.role_key.message} />
          )}
        </div>

        {allowedResources.length > 0 && (
          <div className="space-y-2">
            <Label
              htmlFor="resource-type"
              className="text-sm font-medium text-gray-700"
            >
              Invite user to
            </Label>
            <Controller
              name="resource_type"
              control={form.control}
              render={({ field }) => {
                return (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select where to invite" />
                    </SelectTrigger>
                    <SelectContent>
                      {allowedResources.map(resource => (
                        <SelectItem key={resource} value={resource}>
                          {addSpacesBetweenCapitals(resource)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }}
            />
            {errors.resource_type && (
              <ErrorMessage message={errors.resource_type.message} />
            )}
          </div>
        )}

        {allowedResources.length > 0 && form.watch('resource_type') && (
          <div className="space-y-2">
            <Label
              htmlFor="resource"
              className="text-sm font-medium text-gray-700"
            >
              {addSpacesBetweenCapitals(resourceType!)}
            </Label>
            <Controller
              name="resource_id"
              control={form.control}
              render={({ field }) => {
                return (
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isLoadingResources}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={`Select ${addSpacesBetweenCapitals(resourceType!)}`}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {resources?.map((resource: Resource) => (
                        <SelectItem key={resource.id} value={resource.id}>
                          {resource.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                );
              }}
            />
            {fetchError && (
              <div className="flex items-center justify-between mt-1">
                <p className="text-sm text-red-600">
                  Failed to load resources: {fetchError}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    if (
                      resourceType &&
                      VALID_RESOURCE_TYPES.includes(
                        resourceType as ResourceType
                      )
                    ) {
                      fetchResources(resourceType as ResourceType, {});
                    }
                  }}
                  disabled={isLoadingResources}
                  aria-label="Retry loading resources"
                >
                  Retry
                </Button>
              </div>
            )}
            {errors.resource_id && (
              <ErrorMessage
                message={errors.resource_id.message}
                id="resource-error"
              />
            )}
            {errors.resource_label && (
              <ErrorMessage message={errors.resource_label.message} />
            )}
          </div>
        )}

        <div className="flex justify-end">
          <Button
            className="mr-2"
            onClick={form.handleSubmit(handleFormSubmit)}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Adding...' : 'Add Role Assignment'}
          </Button>
          <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        </div>
      </div>
    </Form>
  );
};

export default RoleAssignmentForm;
